declare var Chart: any;

namespace Matrix.Dashboards {

    export type ChartJsTypes = "line" | "bar" | "pie" | "doughnut" | "polarArea" | "radar" | "scatter" | "bubble";

    export interface ChartWidgetOptions extends IWidgetOptions {
        DatasetCode: string;
        Type: ChartType;
        Series: ChartSeries[];
        LabelField: string;
        ElementClickUrl?: string;
        ElementClickTarget?: string;
    }

    @Serenity.Decorators.registerClass('Matrix.Dashboards.BarChartWidget')
    export class ChartWidget extends Serenity.Widget<ChartWidgetOptions> {

        private data: any[];

        constructor(element: JQuery, opt: any) {
            super(element, opt);

            if (!this.options.DatasetCode) {
                renderSelectDatasetMessage(this.element, this.options.__designer);
                return;
            }

            this.refresh();
        }

        protected refresh() {
            if (!this.options.DatasetCode) {
                this.data = [];
                return;
            }

            DatasetService.Fetch({
                DatasetCode: this.options.DatasetCode,
                Params: this.options.__getDatasetParams()
            },
                (response) => {
                    var data = response.Data || [];
                    if (!Q.isArray(data))
                        data = [data];
                    this.data = data;
                    this.updateContent();
                }, {
                    onError: response => {
                        this.displayError(response?.Error?.Message);
                    }
            });
        }

        static extractSeriesFromColumns(columns: Data.DataColumn[]): ChartSeries[] {
            return columns.slice(1).map((c, idx) => {
                var bgColor = chartColors[idx % chartColors.length];
                return <ChartSeries>{
                    Field: c.Name,
                    Title: c.Title ?? titleize(c.Name),
                    BackgroundColor: bgColor
                };
            });
        }

        static toChartJsType(type: ChartType): ChartJsTypes {
            type = Q.toId(type);
            switch (type) {
                case ChartType.Line:
                    return "line";
                case ChartType.Bar:
                    return "bar";
                case ChartType.Pie:
                    return "pie";
                case ChartType.Doughnut:
                    return "doughnut";
                case ChartType.PolarArea:
                    return "polarArea";
                case ChartType.Radar:
                    return "radar";
                case ChartType.Scatter:
                    return "scatter";
                case ChartType.Bubble:
                    return "bubble";
            }
        }
        
        displayError(message: string) {
            if (message && this.options.__designer)
                Q.notifyError(message);
            this.element.html('<div class="dash-widget-broken"><i class="fa fa-bug"></i></div>')
                .children().attr('title', message);
        }

        protected updateContent(): void {
            this.element.html('');

            if (!this.data)
                return;

            var columns: Data.DataColumn[];

            var serieList = this.options.Series;
            if (serieList == null) {
                columns = columns ?? TableWidget.extractColumnsFromData(this.data).filter(x => x.Visible == null || x.Visible);
                serieList = ChartWidget.extractSeriesFromColumns(columns);
            }

            if (!serieList.length)
                return;

            var labelField = this.options.LabelField;
            if (!labelField) {
                columns = columns ?? TableWidget.extractColumnsFromData(this.data).filter(x => x.Visible == null || x.Visible);
                if (!columns.length)
                    return;
                labelField = columns[0].Name;
            }

            var canvas = $('<canvas style="width: 100%; height: 100%"></canvas>').appendTo(this.element)[0];
            var commonType = ChartWidget.toChartJsType(this.options.Type);

            const data = {
                labels: this.data.map(x => x[labelField]),
                datasets: serieList.map((serie) => {
                    var serieType = ChartWidget.toChartJsType(serie.Type) ?? commonType ?? "line";
                    var borderColor: any = Q.isEmptyOrNull(serie.BackgroundColor) ? undefined : tinycolor(serie.BackgroundColor).lighten(20).toHexString()
                    var backgroundColor: any = Q.isEmptyOrNull(serie.BackgroundColor) ? undefined : serie.BackgroundColor;
                    switch (serieType) {
                        case "doughnut":
                        case "pie":
                        case "polarArea":
                        case "radar":
                            backgroundColor = this.data.map((x, i) => modernColors10[i % modernColors10.length]);
                            borderColor = 'rgba(255, 255, 255, 0.4)';
                            break;
                    }

                    var chartData: any;
                    switch (serieType) {
                        case "bubble":
                            chartData = this.data.map(d => ({
                                y: d[serie.Field],
                                x: d[serie.Field + "__x"] ?? d[labelField],
                                r: d[serie.Field + "__r"]
                            }));
                            break;
                        default:
                            chartData = this.data.map(x => x[serie.Field]);
                    }

                    return {
                        type: serieType,
                        label: serie.Title,
                        data: chartData,
                        borderColor: borderColor,
                        backgroundColor: backgroundColor,
                        fill: Q.isEmptyOrNull(serie.AreaFillColor) ? undefined : {
                            target: 'origin',
                            above: serie.AreaFillColor,
                            below: serie.AreaFillColor
                        }
                    }
                })
            }

            const config = {
                type: commonType,
                data,
                options: {
                    maintainAspectRatio: false,
                    onClick: (e, elements) => {
                        if (!elements)
                            return;

                        var element = elements[0];
                        if (!element ||
                            element.datasetIndex == null ||
                            element.datasetIndex < 0 ||
                            element.datasetIndex >= serieList.length ||
                            element.index == null ||
                            element.index < 0)
                            return;

                        var serie = serieList[element.datasetIndex];
                        var url = serie.ElementClickUrl || this.options.ElementClickUrl;
                        if (!url)
                            return;

                        url = Q.microFormat(url, this.data[element.index] ?? {}, {
                            handler: TemplateUrlFormatter.microFormatUrlHandler
                        });

                        if (url && Q.startsWith(url, '~/'))
                            url = Q.resolveUrl(url);

                        if (!url)
                            return;

                        var target = this.options.ElementClickTarget;
                        if (target && target.length)
                            window.open(url, target);
                        else
                            window.location.href = url;
                    }
                }
            }

            new Chart(canvas, config);
        }
    }

}